import { IContent } from 'vev';
import React, { useContext, useMemo, createContext } from 'react';
import { isString } from '../../utils';

type ModelsContextModel = { [key: string]: IContent };
type VirtualModelContextModel = {
  masterKey: string;
  instanceKey: string;
  instanceKeyChain: string[];
  overrides: { [modelKey: string]: any };
};

const createKeyMap = (models: IContent[]) => {
  const keyMap: { [key: string]: IContent } = {};
  models.forEach((model) => {
    keyMap[model.key] = model;
  });
  return keyMap;
};

const ModelsContext = createContext<ModelsContextModel | null>(null);
const ModelContext = createContext<IContent>({ key: '' });
const VirtualModelContext = createContext<VirtualModelContextModel | null>(null);

export const ModelProvider = ({
  value,
  children,
}: {
  value: IContent;
  children: React.ReactNode;
}) => {
  const masterKey = value.master;
  if (isString(masterKey)) {
    return (
      <ModelContext.Provider value={value}>
        <VirtualModelProvider
          overrides={value.childContent || {}}
          instanceKey={value.key}
          masterKey={masterKey}
        >
          {children}
        </VirtualModelProvider>
      </ModelContext.Provider>
    );
  }
  return <ModelContext.Provider value={value}>{children}</ModelContext.Provider>;
};

export function ModelsProvider({
  models,
  children,
}: {
  models: IContent[];
  children: React.ReactNode;
}) {
  const modelMap = useMemo(() => createKeyMap(models), [models]);
  return <ModelsContext.Provider value={modelMap}>{children}</ModelsContext.Provider>;
}

/**
 * Get the override map for the current virtual model
 * Need to rewrite keys because of old instances used weird instance keys in overrides
 */
function getOverrideMap(
  instanceKey: string,
  childContent?: { [key: string]: any },
): { [key: string]: any } {
  if (!childContent) return {};
  const overrides: { [key: string]: any } = {};
  for (const key in childContent) {
    const keyChain = key.split('-');
    /** Splitting on '-' will fail in cases where the modelKey has that symbol. Not really a good way to find
     * the real key in the chain as old content models vary in length. However newer content keys are 11 characters
     * so we can use this to help find the real key in the chain
     */
    const [newKey] = key[11] === '-' ? [key.slice(0, 11)] : keyChain;
    const lastKey =
      key[key.length - 12] === '-' ? key.slice(key.length - 11) : keyChain[keyChain.length - 1];

    if (!overrides[newKey] || lastKey === instanceKey) {
      overrides[newKey] = childContent[key];
    }
  }

  return overrides;
}

export function VirtualModelProvider({
  overrides,
  masterKey,
  instanceKey,
  children,
}: {
  overrides?: { [modelKey: string]: any };
  masterKey: string;
  instanceKey: string;
  children: React.ReactNode;
}) {
  const virtualParent = useContext(VirtualModelContext);

  const virtualModel: VirtualModelContextModel = useMemo(() => {
    const instanceKeyChain = virtualParent?.instanceKeyChain.slice() || [];
    instanceKeyChain.unshift(instanceKey);

    const parentOverrides = virtualParent?.overrides || {};
    const newOverrides: { [modelKey: string]: any } = getOverrideMap(instanceKey, overrides);

    for (const parentKey in parentOverrides) {
      if (newOverrides[parentKey]) {
        // Parent override is the top instance, so that should override the new overrides
        newOverrides[parentKey] = { ...newOverrides[parentKey], ...parentOverrides[parentKey] };
      } else newOverrides[parentKey] = parentOverrides[parentKey];
    }

    return {
      masterKey,
      instanceKey,
      instanceKeyChain,
      overrides: newOverrides,
    };
  }, [masterKey, instanceKey, virtualParent, overrides]);

  return (
    <VirtualModelContext.Provider value={virtualModel}>{children}</VirtualModelContext.Provider>
  );
}

export function useContextModel(key?: string): IContent | undefined {
  const models = useContext(ModelsContext);
  const contextState = useContext(ModelContext);
  const virtualModel = useContext(VirtualModelContext);
  const model = models?.[key || ''];
  const master = models?.[model?.master?.toString() || ''];

  return useMemo(() => {
    if (!key) return contextState;
    const instanceKeyChain = virtualModel?.instanceKeyChain || [];

    const virtualKey =
      !instanceKeyChain?.length || instanceKeyChain.includes(key)
        ? key
        : `${key}-${virtualModel?.instanceKeyChain.join('-')}`;

    const override = virtualModel?.overrides[key] || virtualModel?.overrides[virtualKey];

    if (override || master) {
      return {
        ...master,
        ...model,
        ...override,
        virtualKey,
        fromModel: key,
        content: { ...master?.content, ...model?.content, ...override?.content },
      };
    } else if (virtualModel) {
      return {
        ...model,
        fromModel: key,
        key: virtualKey,
      };
    }
    return model;
  }, [key, model, virtualModel, contextState, master]);
}

export function useModels() {
  return useContext(ModelsContext);
}
